import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_CSV,
  downloadCampaignCsv,
  invalidateCampaignQueries,
  toCampaignContactRow,
} from '#/entities/campaign'
import type { CampaignContactRow } from '#/entities/campaign'
import {
  useListContactsApiV1CampaignsCampaignIdContactsGet,
  useRemovePeopleApiV1CampaignsCampaignIdPeopleRemovePost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { phoneToDialable } from '#/shared/lib/format'
import { useDebouncedValue } from '#/shared/lib/hooks'
import { useOpenThread } from '#/shared/lib/phone-panel'
import { toast } from '#/shared/ui/sonner'
import type { DataTablePagination } from '#/shared/ui/data-table'

import type { CampaignChip } from './chips'

import {
  CAMPAIGN_CONTACT_ALERT_FILTERS,
  CAMPAIGN_CONTACT_FILTERS,
  CAMPAIGN_CONTACT_FILTER_ORDER,
  campaignContactFilterLabelKey,
} from './contact-filters'
import type { CampaignContactFilter } from './contact-filters'

const PAGE_SIZE = 10
/** Typing pause before the search reaches the service. */
const SEARCH_DEBOUNCE_MS = 300

export type CampaignContactChip = CampaignChip<CampaignContactFilter>

/**
 * The Contact tab's state: which slice is on screen, who is ticked, and where
 * in the list we are. Selection resets when the filter changes — the rows under
 * a tick are not the same rows any more, and silently carrying them forward is
 * how bulk actions hit the wrong people.
 *
 * `GET /campaigns/{id}/contacts` returns every chip count on every request,
 * whatever filter is active, so one call fills the chips and the page.
 */
export function useCampaignContacts(
  campaignId: string,
  initialFilter: CampaignContactFilter = CAMPAIGN_CONTACT_FILTERS.all,
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const openThread = useOpenThread()
  const [filter, setFilter] = useState<CampaignContactFilter>(initialFilter)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const term = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS)

  const query = useListContactsApiV1CampaignsCampaignIdContactsGet(
    campaignId,
    { filter, page, size: PAGE_SIZE, q: term === '' ? undefined : term },
    { query: { placeholderData: (previous) => previous } },
  )
  const data = query.data?.data
  const counts = data?.counts

  const rows: CampaignContactRow[] = useMemo(
    () => (data?.rows ?? []).map(toCampaignContactRow),
    [data],
  )

  const chips: CampaignContactChip[] = useMemo(
    () =>
      CAMPAIGN_CONTACT_FILTER_ORDER.map((key) => ({
        key,
        label: t(campaignContactFilterLabelKey(key)),
        count: counts?.[key] ?? 0,
        alert: CAMPAIGN_CONTACT_ALERT_FILTERS.includes(key),
      })),
    [t, counts],
  )

  const onFilterChange = useCallback((next: CampaignContactFilter) => {
    setFilter(next)
    setSelectedIds([])
    setPage(1)
  }, [])

  const clearSelection = useCallback(() => setSelectedIds([]), [])

  const remove = useRemovePeopleApiV1CampaignsCampaignIdPeopleRemovePost({
    mutation: {
      onSuccess: (response) => {
        toast.success(
          t('campaigns.contact.toasts.removed', {
            count: response.data.removed,
          }),
        )
        clearSelection()
        invalidateCampaignQueries(queryClient)
      },
      onError: (error) =>
        toast.error(
          getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
        ),
    },
  })

  const onRemoveIds = useCallback(
    (enrollmentIds: string[]) => {
      if (enrollmentIds.length === 0) return
      remove.mutate({ campaignId, data: { enrollment_ids: enrollmentIds } })
    },
    [remove, campaignId],
  )

  const onRemoveFromSequence = useCallback(
    () => onRemoveIds(selectedIds),
    [onRemoveIds, selectedIds],
  )

  /** The row menu acts on the row it was opened from, whatever is ticked. */
  const onRemoveRow = useCallback(
    (row: CampaignContactRow) => onRemoveIds([row.id]),
    [onRemoveIds],
  )

  const openRow = useCallback(
    (row: CampaignContactRow) => {
      const phone = phoneToDialable(row.phone)
      if (phone == null) return
      openThread({ phone, name: row.name })
    },
    [openThread],
  )

  /**
   * The phone panel shows one thread at a time, so "Open in Phone" takes the
   * first ticked person; the rest stay selected for the next action.
   */
  const onOpenInPhone = useCallback(() => {
    const first = rows.find((row) => selectedIds.includes(row.id))
    if (first == null) return
    openRow(first)
  }, [rows, selectedIds, openRow])

  const onExport = useCallback(() => {
    downloadCampaignCsv(
      CAMPAIGN_CSV.people(campaignId),
      `campaign-${campaignId}-people.csv`,
    ).catch((error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    )
  }, [campaignId, t])

  const total = data?.total ?? 0
  const pagination: DataTablePagination = {
    page,
    pageSize: PAGE_SIZE,
    totalItems: total,
    totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
    onPageChange: setPage,
    onPageSizeChange: () => undefined,
  }

  return {
    rows,
    isLoading: query.isPending,
    search,
    onSearchChange: setSearch,
    chips,
    filter,
    onFilterChange,
    selectedIds,
    onSelectionChange: setSelectedIds,
    clearSelection,
    summary: data?.summary ?? '',
    pagination,
    onOpenInPhone,
    onOpenRowInPhone: openRow,
    onRemoveFromSequence,
    onRemoveRow,
    onExport,
    isRemoving: remove.isPending,
  }
}

export type UseCampaignContactsResult = ReturnType<typeof useCampaignContacts>
